/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineComponent, PropType, ref, watch, computed } from "vue";

export default defineComponent({
  name: "c-multi-checkbox",
  props: {
    value: {
      type: [String, Number, Array] as PropType<
        string | number | (string | number)[]
      >,
      validator: () => true,
    },
    items: {
      type: Array as PropType<{ children: { [key: string]: any }[] }[]>,
      default: () => [],
    },
    label: {
      type: String as PropType<string>,
      default: "",
    },
    itemValue: {
      type: [String, Number],
      default: undefined,
    },
    itemLabel: {
      type: String as PropType<string>,
      default: "",
    },
    onUpdateValue: {
      type: Function as PropType<(value: string[] | number[]) => void>,
      required: true,
    },
    isDisabled: { type: Boolean, default: false },
  },

  emits: ["update:value"],

  setup(props, { emit }) {
    const selected = ref<(string | number)[]>(
      props.value
        ? Array.isArray(props.value)
          ? props.value.map(String)
          : [String(props.value)]
        : []
    );

    const toggleAllSelected = (checked: boolean) => {
      if (checked) {
        selected.value = props.items.reduce(
          (
            codes: (string | number)[],
            group: { children: { [key: string]: any }[] }
          ) => {
            return codes.concat(
              group?.children.map(
                (child) =>
                  String(child[props.itemValue ?? ""] ?? "") as string | number
              )
            );
          },
          []
        );
      } else {
        selected.value = [];
      }
      emitUpdatedValue();
    };

    const isGroupSelected = (group: { children: { [key: string]: any }[] }) => {
      return group?.children.every((child) =>
        selected.value.includes(
          String(child[props.itemValue ?? ""] ?? "") as string | number
        )
      );
    };

    const toggleGroupSelected = (group: {
      children: { [key: string]: any }[];
    }) => {
      if (isGroupSelected(group)) {
        selected.value = selected.value.filter(
          (id) =>
            !group?.children
              .map(
                (child) =>
                  String(child[props.itemValue ?? ""] ?? "") as string | number
              )
              .includes(String(id))
        );
      } else {
        selected.value = selected.value.concat(
          group?.children.map(
            (child) =>
              String(child[props.itemValue ?? ""] ?? "") as string | number
          )
        );
      }
      emitUpdatedValue();
    };

    const isSelected = (item: { [key: string]: any }) => {
      return selected.value.includes(
        String(item[props.itemValue ?? ""] ?? "") as string | number
      );
    };

    const toggleSelect = (item: { [key: string]: any }) => {
      const index = selected.value.indexOf(
        String(item[props.itemValue ?? ""] ?? "") as string | number
      );

      if (index > -1) {
        selected.value.splice(index, 1);
      } else {
        selected.value.push(
          String(item[props.itemValue ?? ""] ?? "") as string | number
        );
      }
      emitUpdatedValue();
    };

    const emitUpdatedValue = () => {
      const selectedValue = selected.value.map((idString) => Number(idString));
      emit("update:value", selectedValue);
      if (props.onUpdateValue) {
        props.onUpdateValue(selectedValue);
      }
    };

    watch(
      () => props.value,
      (newValue) => {
        selected.value = newValue
          ? Array.isArray(newValue)
            ? newValue.map(String)
            : [String(newValue)]
          : [];
      }
    );

    const selectAll = computed<boolean>({
      get() {
        return selected.value.length === totalSelected.value;
      },
      set(value) {
        toggleAllSelected(value);
      },
    });

    const totalSelected = computed<number>(() => {
      return props.items.reduce((count, group) => {
        return count + (group?.children.length || 0);
      }, 0);
    });

    return {
      selected,
      toggleAllSelected,
      isGroupSelected,
      toggleGroupSelected,
      isSelected,
      toggleSelect,
      emitUpdatedValue,
      selectAll,
      totalSelected,
    };
  },
});
